import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { UploadStep } from "@/components/import/UploadStep";
import { AnalyzingStep } from "@/components/import/AnalyzingStep";
import { MappingStep } from "@/components/import/MappingStep";
import { PreviewStep } from "@/components/import/PreviewStep";
import { DoneStep } from "@/components/import/DoneStep";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export default function ImportPage() {
  const [step, setStep] = useState("upload");
  const [platform, setPlatform] = useState("Shopify");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [batch, setBatch] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const clearStaging = (stagingId) => {
    if (stagingId) api.delete(`/imports/staging/${stagingId}`).catch(() => {});
  };

  const showMapping = useCallback(async (data) => {
    const { data: tpls } = await api.get("/mapping-templates");
    setTemplates(tpls);
    setMapping({});
    setStep("mapping");
    toast.info("We didn't recognize this file's layout. Please match the columns below.");
  }, []);

  const handlePreview = useCallback(async (data) => {
    setPreview(data);
    if (data.status === "failed") {
      toast.error(data.error || "We could not analyze this file. Please check it and try again.");
      clearStaging(data.staging_id);
      setStep("upload");
      setPreview(null);
    } else if (data.status === "analyzing") {
      setStep("analyzing");
    } else if (data.needs_mapping) {
      await showMapping(data);
    } else {
      setStep("preview");
    }
  }, [showMapping]);

  const stagingId = step === "analyzing" ? preview?.staging_id : null;
  useEffect(() => {
    if (!stagingId) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/imports/staging/${stagingId}`);
        if (data.status !== "analyzing") {
          clearInterval(t);
          await handlePreview(data);
        }
      } catch (e) {
        clearInterval(t);
        toast.error(apiError(e));
        setStep("upload");
        setPreview(null);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [stagingId, handlePreview]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_platform", platform);
      const { data } = await api.post("/imports/upload", fd);
      await handlePreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async () => {
    setBusy(true);
    try {
      const body = { mapping, template_name: templateName.trim() || null };
      const { data } = await api.post(`/imports/${preview.staging_id}/apply-mapping`, body);
      await handlePreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${preview.staging_id}/commit`);
      setStep("processing");
      pollRef.current = setInterval(async () => {
        const { data: b } = await api.get(`/imports/${data.batch_id}`);
        setBatch(b);
        if (b.status === "completed") {
          clearInterval(pollRef.current);
          setStep("done");
        } else if (b.status === "failed") {
          clearInterval(pollRef.current);
          clearStaging(preview.staging_id);
          toast.error("Something went wrong partway through this import. It was marked as failed — you can undo it from Import History and try again.");
          setStep("upload");
          setPreview(null);
        }
      }, 800);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadErrors = async () => {
    const res = await api.get(`/imports/${batch.id}/errors`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    if (preview?.staging_id && ["preview", "mapping", "analyzing"].includes(step)) clearStaging(preview.staging_id);
    setStep("upload");
    setPreview(null);
    setBatch(null);
    setMapping({});
    setTemplateName("");
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="import-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Import Orders</h1>
        <p className="text-muted-foreground mt-2">Upload an order export from Shopify or PayPal. Nothing is saved until you confirm.</p>
      </div>

      {step === "upload" && (
        <UploadStep platform={platform} setPlatform={setPlatform} busy={busy} onFile={upload} />
      )}

      {step === "analyzing" && <AnalyzingStep preview={preview} onCancel={reset} />}

      {step === "mapping" && preview && (
        <MappingStep
          headers={preview.headers} mapping={mapping} setMapping={setMapping}
          templates={templates} templateName={templateName} setTemplateName={setTemplateName}
          busy={busy} onApply={applyMapping} onReset={reset}
        />
      )}

      {step === "preview" && preview?.counts && (
        <PreviewStep counts={preview.counts} failedRows={preview.failed_rows} busy={busy} onCommit={commit} onReset={reset} />
      )}

      {step === "processing" && (
        <Card className="shadow-sm fade-in" data-testid="processing-panel">
          <CardContent className="p-8 space-y-4">
            <h2 className="text-2xl font-serif font-semibold">Importing your orders…</h2>
            <Progress value={batch && batch.total ? (batch.progress / batch.total) * 100 : 5} data-testid="import-progress" />
            <p className="text-sm text-muted-foreground">
              {batch ? `${Math.min(batch.progress, batch.total)} of ${batch.total} orders processed` : "Starting…"}
            </p>
          </CardContent>
        </Card>
      )}

      {step === "done" && batch && (
        <DoneStep batch={batch} onDownloadErrors={downloadErrors} onReset={reset} />
      )}
    </div>
  );
}
